import { useCallback, useEffect, useState } from 'react';
import { socialApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import type { SocialAutomationRule } from '@/types/social';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

function parseActionType(rule: SocialAutomationRule): string {
  if (!rule.actionJson) return 'AUTO_REPLY';
  try {
    const parsed = JSON.parse(rule.actionJson) as { type?: string };
    return parsed.type ?? 'AUTO_REPLY';
  } catch {
    return 'AUTO_REPLY';
  }
}

function parsePlatform(rule: SocialAutomationRule): string {
  if (!rule.conditionJson) return 'ALL';
  try {
    const parsed = JSON.parse(rule.conditionJson) as { platform?: string };
    return parsed.platform ?? 'ALL';
  } catch {
    return 'ALL';
  }
}

export function SocialAutomationPage() {
  const [rules, setRules] = useState<SocialAutomationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [trigger, setTrigger] = useState('INBOUND_MESSAGE');
  const [action, setAction] = useState('AUTO_REPLY');
  const [platform, setPlatform] = useState('ALL');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRules(await socialApi.listAutomationRules());
    } catch (err) {
      showApiError(err, 'Failed to load automation rules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const addRule = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await socialApi.createAutomationRule({
        name: name.trim(),
        triggerEvent: trigger,
        actionType: action,
        platform: platform === 'ALL' ? undefined : platform,
        active: true,
      });
      setName('');
      notify.success('Rule saved');
      void load();
    } catch (err) {
      showApiError(err, 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const removeRule = async (id: number) => {
    try {
      await socialApi.deleteAutomationRule(id);
      notify.success('Rule removed');
      void load();
    } catch (err) {
      showApiError(err, 'Failed to delete rule');
    }
  };

  const toggleRule = async (rule: SocialAutomationRule) => {
    try {
      await socialApi.toggleAutomationRule(rule.id, !rule.active);
      void load();
    } catch (err) {
      showApiError(err, 'Failed to update rule');
    }
  };

  return (
    <div>
      <PageHeader
        title="Automation Center"
        description="Auto-replies, assignment, lead routing, review routing, escalation, and sentiment rules — stored securely per organization"
        breadcrumbs={[{ label: 'Social', to: '/social' }, { label: 'Automation' }]}
      />

      <div className="card mb-6 grid gap-4 p-4 sm:grid-cols-2 lg:grid-cols-4">
        <Input label="Rule name" value={name} onChange={(e) => setName(e.target.value)} />
        <Select
          label="Trigger"
          value={trigger}
          onChange={setTrigger}
          options={[
            { value: 'INBOUND_MESSAGE', label: 'Inbound message' },
            { value: 'COMMENT', label: 'New comment' },
            { value: 'REVIEW', label: 'New review' },
            { value: 'NEGATIVE_SENTIMENT', label: 'Negative sentiment' },
          ]}
        />
        <Select
          label="Action"
          value={action}
          onChange={setAction}
          options={[
            { value: 'AUTO_REPLY', label: 'Auto reply' },
            { value: 'ASSIGN', label: 'Auto assignment' },
            { value: 'LEAD_ROUTE', label: 'Lead routing' },
            { value: 'COMMENT_ROUTE', label: 'Comment routing' },
            { value: 'REVIEW_ROUTE', label: 'Review routing' },
            { value: 'ESCALATE', label: 'Escalation' },
          ]}
        />
        <Select
          label="Platform"
          value={platform}
          onChange={setPlatform}
          options={[
            { value: 'ALL', label: 'All platforms' },
            { value: 'FACEBOOK', label: 'Facebook' },
            { value: 'INSTAGRAM', label: 'Instagram' },
            { value: 'WHATSAPP', label: 'WhatsApp' },
            { value: 'LINKEDIN', label: 'LinkedIn' },
            { value: 'X', label: 'X' },
          ]}
        />
        <div className="flex items-end sm:col-span-2 lg:col-span-4">
          <Button onClick={() => void addRule()} loading={saving}>
            Add rule
          </Button>
        </div>
      </div>

      {loading && <p className="text-sm text-slate-500">Loading rules…</p>}
      <ul className="space-y-2">
        {!loading && rules.length === 0 && (
          <li className="card p-6 text-sm text-slate-500">
            No automation rules yet. Rules run on the server when webhooks and inbox events match your triggers.
          </li>
        )}
        {rules.map((r) => (
          <li key={r.id} className="card flex flex-wrap items-center justify-between gap-2 p-4 text-sm">
            <div>
              <p className="font-medium">{r.name}</p>
              <p className="text-xs text-slate-500">
                {r.triggerEvent} → {parseActionType(r)} · {parsePlatform(r)}
                {!r.active && ' · Paused'}
              </p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={() => void toggleRule(r)}>
                {r.active ? 'Pause' : 'Activate'}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => void removeRule(r.id)}>
                Remove
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
